import { useScrollProgress, mapRange } from '../heroSignals';

/**
 * Chapter 002 stage
 * ─────────────────
 * As the user scrolls past 0.75, the 3D model shrinks slightly and glides
 * toward the left side, sitting inside a dark rounded "reference card".
 * The lower half of the model tints with a Slate Green colorway to signal
 * this is a specific colorway variant of the AX-07.
 *
 * On the right, three tiers of story copy reveal line-by-line as scroll
 * progresses:
 *   • Chapter eyebrow (mint accent, small caps)
 *   • Chapter title (pure white, display size, heaviest weight)
 *   • Body paragraph #1 — brand philosophy (light gray, regular)
 *   • Craftsmanship eyebrow tag (mint accent, small caps)
 *   • Body paragraph #2 — craftsmanship facts (muted gray)
 */

// Card frame position (matches the model target in ProductShowcase:
// horizontal -28% of viewport width, so the card is centered around ~22vw).
// The card is wider now so the enlarged model reads as the visual subject
// inside it with minimal negative space.
const CARD_CENTER_X_VW = 22;
const CARD_WIDTH_VW = 36;
const CARD_HEIGHT_VH = 68;
// Extra rightward offset — both the card and the model shift together by
// this many pixels to sit slightly more toward the center of the viewport.
const CARD_EXTRA_OFFSET_X_PX = 60;

// Utility: reveal a single line with overflow-clip + translateY
function RevealLine({
  t,
  children,
  style,
}: {
  t: number;
  children: React.ReactNode;
  style?: React.CSSProperties;
}) {
  return (
    <div className="overflow-hidden" style={{ padding: '0.06em 0.02em' }}>
      <div
        style={{
          transform: `translateY(${(1 - t) * 105}%)`,
          transition: 'transform 900ms cubic-bezier(0.22,1,0.36,1)',
          willChange: 'transform',
          ...style,
        }}
      >
        {children}
      </div>
    </div>
  );
}

// Utility: reveal a small eyebrow tag (single row of caps)
function RevealEyebrow({
  t,
  color = '#7AA8EE',
  children,
  marginBottom = 18,
}: {
  t: number;
  color?: string;
  children: React.ReactNode;
  marginBottom?: number;
}) {
  return (
    <div
      className="uppercase overflow-hidden"
      style={{ marginBottom: `${marginBottom}px`, height: '14px' }}
    >
      <div
        style={{
          color,
          fontSize: '11px',
          letterSpacing: '0.32em',
          fontWeight: 500,
          transform: `translateY(${(1 - t) * 100}%)`,
          transition: 'transform 800ms cubic-bezier(0.22,1,0.36,1)',
        }}
      >
        {children}
      </div>
    </div>
  );
}

export default function ChapterTwoStage() {
  const scroll = useScrollProgress();

  // Card fade-in is aligned with the model migration window
  // (ProductShowcase uses 0.62 → 0.90). Card starts appearing right as
  // the model begins shrinking so both animate together — the model
  // literally settles INTO the card as it fades in.
  const stageT = mapRange(scroll, 0.64, 0.76);
  if (stageT <= 0) return null;

  // Right-side text: begins revealing after the model has visibly
  // migrated most of the way to the card (~70% through the migration).
  const textT = mapRange(scroll, 0.80, 0.96);

  // Per-block reveal timings (staggered across the textT window)
  const tChapterEyebrow = mapRange(textT, 0.00, 0.12);
  const tTitleLine1 = mapRange(textT, 0.04, 0.22);
  const tTitleLine2 = mapRange(textT, 0.10, 0.28);

  const tPara1L1 = mapRange(textT, 0.24, 0.44);
  const tPara1L2 = mapRange(textT, 0.30, 0.50);
  const tPara1L3 = mapRange(textT, 0.36, 0.56);
  const tPara1L4 = mapRange(textT, 0.42, 0.62);

  const tCraftEyebrow = mapRange(textT, 0.56, 0.68);
  const tPara2L1 = mapRange(textT, 0.60, 0.76);
  const tPara2L2 = mapRange(textT, 0.66, 0.82);
  const tPara2L3 = mapRange(textT, 0.72, 0.88);
  const tPara2L4 = mapRange(textT, 0.78, 0.94);

  const tailT = mapRange(textT, 0.85, 0.98);

  return (
    <div
      className="pointer-events-none absolute inset-0 z-[8]"
      style={{
        fontFamily:
          'Inter, "Helvetica Neue", Helvetica, Arial, system-ui, sans-serif',
      }}
    >
      {/* ── Left: dark card that "frames" the shrunk model ── */}
      <div
        className="absolute"
        style={{
          left: `calc(${CARD_CENTER_X_VW - CARD_WIDTH_VW / 2}vw + ${CARD_EXTRA_OFFSET_X_PX}px)`,
          top: `${(100 - CARD_HEIGHT_VH) / 2}vh`,
          width: `${CARD_WIDTH_VW}vw`,
          height: `${CARD_HEIGHT_VH}vh`,
          background:
            'linear-gradient(160deg, rgba(30, 62, 130, 0.98) 0%, rgba(20, 46, 108, 0.98) 55%, rgba(12, 30, 78, 0.98) 100%)',
          border: '1px solid rgba(122, 168, 238, 0.28)',
          borderRadius: '18px',
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)',
          boxShadow:
            'inset 0 1px 0 rgba(180, 210, 255, 0.22), inset 0 -1px 0 rgba(0, 0, 0, 0.3), 0 30px 80px -20px rgba(20, 46, 108, 0.6)',
          opacity: stageT,
          transform: `translateY(${(1 - stageT) * 28}px)`,
        }}
      >
        {/* Corner tick marks — small design detail for framing feel */}
        {[
          { top: 10, left: 10, borders: 'top-left' },
          { top: 10, right: 10, borders: 'top-right' },
          { bottom: 10, left: 10, borders: 'bottom-left' },
          { bottom: 10, right: 10, borders: 'bottom-right' },
        ].map((c, i) => (
          <span
            key={i}
            style={{
              position: 'absolute',
              width: '18px',
              height: '18px',
              top: (c as { top?: number }).top,
              left: (c as { left?: number }).left,
              right: (c as { right?: number }).right,
              bottom: (c as { bottom?: number }).bottom,
              borderTop:
                c.borders.startsWith('top')
                  ? '1px solid rgba(170, 205, 245, 0.45)'
                  : undefined,
              borderBottom:
                c.borders.startsWith('bottom')
                  ? '1px solid rgba(170, 205, 245, 0.45)'
                  : undefined,
              borderLeft: c.borders.endsWith('left')
                ? '1px solid rgba(170, 205, 245, 0.45)'
                : undefined,
              borderRight: c.borders.endsWith('right')
                ? '1px solid rgba(170, 205, 245, 0.45)'
                : undefined,
            }}
          />
        ))}

        {/* Bottom-left meta label inside the card */}
        <div
          className="absolute uppercase"
          style={{
            left: '18px',
            bottom: '18px',
            color: 'rgba(190, 215, 245, 0.65)',
            fontSize: '10px',
            letterSpacing: '0.32em',
            fontWeight: 500,
          }}
        >
          AX-07 · REFERENCE UNIT
        </div>
      </div>

      {/* ── Right: story copy with clear hierarchical layers ── */}
      <div
        className="absolute"
        style={{
          right: '6vw',
          top: '50%',
          transform: 'translateY(-50%)',
          maxWidth: '460px',
          width: '38vw',
          opacity: stageT,
        }}
      >
        {/* Chapter eyebrow (mint accent — small caps) */}
        <RevealEyebrow t={tChapterEyebrow} color="#7AA8EE" marginBottom={20}>
          CHAPTER · 002
        </RevealEyebrow>

        {/* Chapter title — pure white, biggest, boldest */}
        <div style={{ marginBottom: '48px' }}>
          <RevealLine
            t={tTitleLine1}
            style={{
              color: '#FFFFFF',
              fontSize: '54px',
              fontWeight: 700,
              lineHeight: 1.04,
              letterSpacing: '-0.02em',
            }}
          >
            A Philosophy of
          </RevealLine>
          <RevealLine
            t={tTitleLine2}
            style={{
              color: '#FFFFFF',
              fontSize: '54px',
              fontWeight: 700,
              lineHeight: 1.04,
              letterSpacing: '-0.02em',
            }}
          >
            Precision.
          </RevealLine>
        </div>

        {/* Paragraph #1 — brand philosophy (light gray, regular) */}
        <div style={{ marginBottom: '44px' }}>
          {[
            { t: tPara1L1, text: 'AXIOM represents the quiet meeting point' },
            { t: tPara1L2, text: 'of precision engineering and pure sound —' },
            { t: tPara1L3, text: 'each component earning its place through' },
            { t: tPara1L4, text: 'restraint rather than announcement.' },
          ].map((row, i) => (
            <RevealLine
              key={i}
              t={row.t}
              style={{
                color: '#D0D4DC',
                fontSize: '19px',
                fontWeight: 400,
                lineHeight: 1.7,
                letterSpacing: '0.005em',
              }}
            >
              {row.text}
            </RevealLine>
          ))}
        </div>

        {/* Craftsmanship section eyebrow (mint accent — small caps) */}
        <RevealEyebrow t={tCraftEyebrow} color="#7AA8EE" marginBottom={16}>
          CRAFTSMANSHIP
        </RevealEyebrow>

        {/* Paragraph #2 — factual craftsmanship copy (muted gray) */}
        <div>
          {[
            { t: tPara2L1, text: 'We build every AX-07 in a single studio' },
            { t: tPara2L2, text: 'in Kanazawa. Titanium is milled, tuned' },
            { t: tPara2L3, text: 'and hand-finished until nothing remains' },
            { t: tPara2L4, text: 'but the necessary. Nothing more.' },
          ].map((row, i) => (
            <RevealLine
              key={i}
              t={row.t}
              style={{
                color: '#9AA0AC',
                fontSize: '17px',
                fontWeight: 400,
                lineHeight: 1.7,
                letterSpacing: '0.005em',
              }}
            >
              {row.text}
            </RevealLine>
          ))}
        </div>

        {/* Subtle tail rule — visual anchor under the copy */}
        <div
          style={{
            marginTop: '28px',
            width: '80px',
            height: '1px',
            background:
              'linear-gradient(to right, rgba(122,168,238,0.6), rgba(122,168,238,0))',
            opacity: tailT,
          }}
        />
      </div>
    </div>
  );
}